/**
 * React Hooks for Workflow Entities
 * 
 * Provides hooks for all workflow entity types
 */

import { useCallback, useEffect, useState } from 'react';
import { executionResultRepository } from '../repositories/ExecutionResultRepository';
import { 
    ExecutionResult, 
    TimelineItem, 
    JobStatus, 
    TransitionTemplate, 
    Integration, 
    Schedule, 
    WorkflowContext 
} from '../types/WorkflowEntities';

// Execution Result Hooks
export function useExecutionResults(instanceId?: string) {
    const [results, setResults] = useState<ExecutionResult[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const fetchResults = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const data = instanceId 
                ? await executionResultRepository.findByInstanceId(instanceId)
                : await executionResultRepository.findAll();
            setResults(data);
        } catch (err) {
            setError(err as Error);
            setResults([]);
        } finally {
            setLoading(false);
        }
    }, [instanceId]);

    useEffect(() => {
        fetchResults();
    }, [fetchResults]);

    return {
        results,
        loading,
        error,
        refetch: fetchResults
    };
}

export function useExecutionResult(resultId: string | null) {
    const [result, setResult] = useState<ExecutionResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const fetchResult = useCallback(async () => {
        if (!resultId) {
            setResult(null);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const data = await executionResultRepository.findById(resultId);
            setResult(data);
        } catch (err) {
            setError(err as Error);
            setResult(null);
        } finally {
            setLoading(false);
        }
    }, [resultId]);

    useEffect(() => {
        fetchResult();
    }, [fetchResult]);

    return {
        result,
        loading,
        error,
        refetch: fetchResult
    };
}

export function useExecutionStatistics() {
    const [statistics, setStatistics] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        const fetchStatistics = async () => {
            setLoading(true);
            setError(null);

            try {
                const data = await executionResultRepository.getStatistics();
                setStatistics(data);
            } catch (err) {
                setError(err as Error);
                setStatistics(null);
            } finally {
                setLoading(false);
            }
        };

        fetchStatistics();
    }, []);

    return {
        statistics,
        loading,
        error
    };
}

// Additional entity hooks would go here for:
// - useTimeline/useTimelineItems
// - useJobStatus/useJobStatuses  
// - useTransitionTemplates
// - useIntegrations
// - useSchedules
// - useWorkflowContext

export default {
    useExecutionResults,
    useExecutionResult,
    useExecutionStatistics
};